import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  TextField,
  CardContent,
  Card,
  Tooltip,
} from "@mui/material";
import {
  columns,
  summaryColumns,
  tabLabels,
  componentSettings,
} from "./columns";
import { usePiping } from "../../PipingContext";
import {
  estimate,
  round2,
  UNITS_ENUM,
  isLengthBased,
  getUnitForComponent,
  buildSummaryRows,
} from "./utils";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { secondAxios } from "../../../../../../api/axios";

import { MaterialReactTable } from "material-react-table";
import {
  mrtBodyCellSx,
  mrtHeadCellSx,
  useMrtSurfaceProps,
} from "../../../../../../styles/mrtThemeProps";
// ─── Affected columns per unit system (for tooltip) ─────────────────────────
const PIPING_UNIT_COLS = [
  { label: "Unit Weight", imp: "lbs/unit", met: "kg/unit" },
  { label: "Unit Cost", imp: "$/unit or $/lb", met: "$/unit or $/kg" },
  { label: "Total Weight", imp: "lbs", met: "kg" },
  { label: "BOM Weight", imp: "lb", met: "kg" },
];

// The two panels sit side by side and must line up row-for-row, so every
// vertical metric that affects alignment is fixed and shared between them:
// same header height, same row height, same viewport height. Anything that
// lets one panel's row grow (wrapping text, a taller control) breaks the
// alignment that the synced scrolling depends on.
const PANEL_ROW_HEIGHT = 40;
const PANEL_HEAD_HEIGHT = 66;
const PANEL_BODY_MAX_HEIGHT = 470;

const ESTIMATED_PANEL_WIDTH = 380;

/*
 * Same visual system used in Shell / Nozzles.
 */
// Theme-aware: the light lavender header is only right in light mode.
const estimationHeaderBackground = (theme) =>
  theme.palette.mode === "dark"
    ? theme.palette.background.default
    : "hsla(250, 86%, 97%, 1)";

const ESTIMATION_TABLE_BORDER = "1px solid rgba(170, 180, 198, 0.6)";

const PANEL_FONT_SIZE = "clamp(11px, 0.72vw, 13px)";

const PANEL_HEADER_FONT_SIZE = "clamp(11px, 0.75vw, 14px)";
/*
 * MRT merges cell props with a shallow Object.assign, so a column's own
 * `muiTableBodyCellProps.sx` REPLACES the table-level one rather than
 * merging into it. Every column here carries an sx (columns.jsx adds one),
 * which means table-level cell styling never reaches them. So the shared
 * panel styling has to be folded into each column definition instead.
 */

const panelBodyCellSx = (isFirstPanel) => ({
  ...mrtBodyCellSx,

  boxSizing: "border-box",

  height: `${PANEL_ROW_HEIGHT}px !important`,
  minHeight: `${PANEL_ROW_HEIGHT}px !important`,
  maxHeight: `${PANEL_ROW_HEIGHT}px !important`,

  minWidth: 0,
  maxWidth: "none",

  px: isFirstPanel ? "4px" : "2px",
  py: "0 !important",

  verticalAlign: "middle",

  borderRight: `${ESTIMATION_TABLE_BORDER} !important`,
  borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,

  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",

  textAlign: "center !important",

  fontSize: `${PANEL_FONT_SIZE} !important`,
  fontWeight: "500 !important",
  lineHeight: "1.2 !important",

  "& .Mui-TableBodyCell-Content-Wrapper": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    textAlign: "center !important",

    overflow: "hidden",
    textOverflow: "ellipsis",

    fontSize: `${PANEL_FONT_SIZE} !important`,
    fontWeight: "500 !important",
    lineHeight: "1.2 !important",
  },

  "& .Mui-TableBodyCell-Content-Wrapper > *:not(.MuiSvgIcon-root)": {
    minWidth: 0,

    fontSize: `${PANEL_FONT_SIZE} !important`,
    fontWeight: "500 !important",
    lineHeight: "1.2 !important",

    textAlign: "center !important",
  },

  /*
   * TextField
   */
  "& .MuiInputBase-root": {
    width: "100%",
    minWidth: 0,

    minHeight: "28px",
    height: "28px",

    fontSize: `${PANEL_FONT_SIZE} !important`,
  },

  "& .MuiInputBase-input": {
    width: "100%",
    minWidth: 0,

    boxSizing: "border-box",

    paddingTop: "2px",
    paddingBottom: "2px",

    textAlign: "center !important",

    fontSize: `${PANEL_FONT_SIZE} !important`,
  },

  /*
   * Select / EditableSelectCell
   */
  "& .MuiSelect-select": {
    width: "100%",
    minWidth: 0,

    minHeight: "unset !important",

    paddingTop: "2px !important",
    paddingBottom: "2px !important",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    whiteSpace: "nowrap !important",
    overflow: "hidden !important",
    textOverflow: "ellipsis",
    wordBreak: "normal !important",

    textAlign: "center !important",

    fontSize: `${PANEL_FONT_SIZE} !important`,
  },

  "& .MuiSelect-icon": {
    fontSize: "0.9rem",
    right: 0,
  },

  "& .MuiButton-root": {
    minHeight: "28px",
    height: "28px",

    fontSize: `${PANEL_FONT_SIZE} !important`,
  },

  "& .MuiIconButton-root": {
    padding: "2px",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
});

const panelHeadCellSx = {
  ...mrtHeadCellSx,

  boxSizing: "border-box",

  height: `${PANEL_HEAD_HEIGHT}px !important`,
  minHeight: `${PANEL_HEAD_HEIGHT}px !important`,
  maxHeight: `${PANEL_HEAD_HEIGHT}px !important`,

  minWidth: 0,
  maxWidth: "none",

  px: "4px !important",
  py: "2px !important",

  verticalAlign: "middle",

  backgroundColor: (theme) => `${estimationHeaderBackground(theme)} !important`,

  borderRight: `${ESTIMATION_TABLE_BORDER} !important`,

  borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,

  color: "text.primary",

  textAlign: "center",

  fontWeight: "700 !important",

  fontSize: `${PANEL_HEADER_FONT_SIZE} !important`,

  lineHeight: "1.1 !important",

  overflow: "hidden",

  "& .Mui-TableHeadCell-Content": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    overflow: "hidden",
  },

  "& .Mui-TableHeadCell-Content-Labels": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    overflow: "hidden",
  },

  "& .Mui-TableHeadCell-Content-Wrapper": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",

    textAlign: "center !important",

    whiteSpace: "normal !important",

    overflow: "hidden !important",

    fontSize: `${PANEL_HEADER_FONT_SIZE} !important`,

    fontWeight: "700 !important",

    lineHeight: "1.1 !important",
  },

  "& .Mui-TableHeadCell-Content-Wrapper > *": {
    width: "100% !important",

    margin: "0 !important",

    textAlign: "center !important",

    fontSize: "inherit !important",
    fontWeight: "inherit !important",
    lineHeight: "inherit !important",
  },
};

// Fold the shared panel styling into each column, letting it win over the
// column's own sx for the keys that decide alignment.
function withPanelCellStyles(tableColumns, isFirstPanel) {
  const bodySx = panelBodyCellSx(isFirstPanel);

  return tableColumns.map((col) => ({
    ...col,
    muiTableBodyCellProps: {
      ...(col.muiTableBodyCellProps || {}),
      sx: { ...(col.muiTableBodyCellProps?.sx || {}), ...bodySx },
    },
    muiTableHeadCellProps: {
      ...(col.muiTableHeadCellProps || {}),
      sx: { ...(col.muiTableHeadCellProps?.sx || {}), ...panelHeadCellSx },
    },
  }));
}

function withStandaloneTableStyles(tableColumns) {
  return (tableColumns || []).map((col) => ({
    ...col,

    /*
     * Keep the declared `size` as the relative preferred width,
     * but allow every column to grow and fill the table.
     */
    minSize: 1,
    maxSize: 100000,
    grow: true,

    muiTableHeadCellProps: {
      ...(col.muiTableHeadCellProps || {}),

      sx: {
        ...(col.muiTableHeadCellProps?.sx || {}),
        ...panelHeadCellSx,

        minWidth: "0 !important",
        maxWidth: "none !important",
      },
    },

    muiTableBodyCellProps: {
      ...(col.muiTableBodyCellProps || {}),

      sx: {
        ...(col.muiTableBodyCellProps?.sx || {}),
        ...panelBodyCellSx(true),

        minWidth: "0 !important",
        maxWidth: "none !important",
      },
    },
  }));
}

function normalizeUnit(unit) {
  if (!unit) return "";
  switch (unit) {
    case "Ft":
      return "ft";
    case "M":
      return "m";
    case "Ft^2":
    case "Ft²":
      return "ft²";
    case "M^2":
    case "M²":
      return "m²";
    case "No.":
    case "Nos.":
      return "Nos";
    default:
      return unit; // already correct
  }
}

function UnitToggle({ isMetric, onChange }) {
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Box
        sx={{
          fontSize: "0.7rem",
          color: "text.secondary",
          mb: 0.75,
          fontWeight: 500,
        }}
      >
        Columns that change
      </Box>
      {PIPING_UNIT_COLS.map((c) => (
        <Box
          key={c.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            fontSize: "0.7rem",
            py: "2px",
          }}
        >
          <span style={{ opacity: 0.75 }}>{c.label}</span>
          <span style={{ fontWeight: 500 }}>{isMetric ? c.met : c.imp}</span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom-start" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          border: "0.5px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          cursor: "default",
        }}
      >
        {/* Imperial button */}
        <Box
          onClick={() => onChange(false)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: "0.8rem",
            fontWeight: isMetric ? 400 : 500,
            color: isMetric ? "text.secondary" : "primary.main",
            bgcolor: isMetric ? "transparent" : "action.selected",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.hover" : "action.selected",
            },
          }}
        >
          Imperial
          <Box
            component="span"
            sx={{
              fontSize: "0.65rem",
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "transparent" : "#E6F1FB",
              color: isMetric ? "text.disabled" : "#185FA5",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            ft · lb
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ width: "0.5px", height: 20, bgcolor: "divider" }} />

        {/* Metric button */}
        <Box
          onClick={() => onChange(true)}
          sx={{
            px: 1.5,
            py: 0.6,
            fontSize: "0.8rem",
            fontWeight: isMetric ? 500 : 400,
            color: isMetric ? "primary.main" : "text.secondary",
            bgcolor: isMetric ? "action.selected" : "transparent",
            cursor: "pointer",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            transition: "all 0.15s",
            "&:hover": {
              bgcolor: isMetric ? "action.selected" : "action.hover",
            },
          }}
        >
          Metric
          <Box
            component="span"
            sx={{
              fontSize: "0.65rem",
              px: 0.75,
              py: "1px",
              borderRadius: 99,
              bgcolor: isMetric ? "#E1F5EE" : "transparent",
              color: isMetric ? "#0F6E56" : "text.disabled",
              fontWeight: 500,
              transition: "all 0.15s",
            }}
          >
            m · kg
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

export function EstimationTable({ revision, wbsUid }) {
  const mrtSurfaceProps = useMrtSurfaceProps();
  const [tabIndex, setTabIndex] = useState(0);
  const [fittingsWeld, setFittingsWeld] = useState(1);
  const [pipeWeld, setPipeWeld] = useState(1);
  const [isMetric, setIsMetric] = useState(false);
  const [showEstimatedBulkTable, setShowEstimatedBulkTable] = useState(false);
  const currentTab = tabLabels[tabIndex];
  const { id } = useParams();

  // ─── Synced vertical scrolling between the two side-by-side panels ─────────
  // Both tables render the same rows at the same fixed row height, so mirroring
  // scrollTop keeps a row on the left visually level with its estimated values
  // on the right. The guard stops the mirrored scroll from echoing back.
  const firstPanelScrollRef = useRef(null);
  const secondPanelScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  const syncPanelScroll = (sourceRef, targetRef) => () => {
    if (isSyncingScrollRef.current) return;
    const source = sourceRef.current;
    const target = targetRef.current;
    if (!source || !target || source.scrollTop === target.scrollTop) return;

    isSyncingScrollRef.current = true;
    target.scrollTop = source.scrollTop;
    // Release on the next frame, after the mirrored scroll event has fired.
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  };

  const {
    getSelectedSpecTable,
    SelectedParts,
    allTables,
    estimationTable,
    setEstimationTable,
    dataSheetsStore,
    uidOptions,
    uidRows,
    specLibraryTable,
  } = usePiping();

  // Always start each revision with Estimated values hidden, even if saved
  // estimation values already exist in state. It is shown only by Estimate.
  useEffect(() => {
    setShowEstimatedBulkTable(false);
  }, [revision]);

  // ─── Convert DB fields in state when toggling unit system ───────────────────
  // DB stores metric (kg, $/kg). On toggle we convert Weight, UnitCost,
  // TotalWeight in both Bulk and Tag rows so columns can render raw values.
  function handleUnitToggle(newIsMetric) {
    if (newIsMetric === isMetric) return;

    const toMetric = newIsMetric; // true  = going metric
    const weightFactor = toMetric ? 1 / 2.20462 : 2.20462; // lbs↔kg
    // const costFactor = toMetric ? 2.20462 : 1 / 2.20462; // $/lb↔$/kg

    const convertRow = (row) => {
      const next = { ...row };

      // Re-derive unit based on component type and new unit system
      next.Unit = getUnitForComponent(next.Components, toMetric);

      if (
        next.Weight !== undefined &&
        next.Weight !== null &&
        next.Weight !== "" &&
        next.Weight !== "N/A"
      ) {
        next.Weight = +(Number(next.Weight) * weightFactor).toFixed(4);
      }
      // Only convert UnitCost if the unit is length-based ($/kg ↔ $/lb)
      // Nos items are priced $/unit so cost doesn't change with unit system
      const isNos = !isLengthBased(next.Components);

      if (
        next.TotalWeight !== undefined &&
        next.TotalWeight !== null &&
        next.TotalWeight !== "" &&
        next.TotalWeight !== "N/A"
      ) {
        next.TotalWeight = +(Number(next.TotalWeight) * weightFactor).toFixed(
          4,
        );
      }

      return next;
    };

    setEstimationTable((prev) => {
      const rev = prev?.[revision] || {};

      const freshBulk = (rev["Bulk Materials"] || []).map(convertRow);
      const freshTag = (rev["Tag Materials"] || []).map(convertRow);

      // Regenerate BOM from the already-converted rows so totWeight stays in sync.
      // Summary needs no conversion of its own — its 2.x/3.x rows are derived
      // live from Bulk/Tag Materials (see buildSummaryRows), so they pick up
      // the new unit system automatically.
      const freshBOM = generateBOMData(freshBulk, freshTag);

      return {
        ...prev,
        [revision]: {
          ...rev,
          "Bulk Materials": freshBulk,
          "Tag Materials": freshTag,
          BOM: freshBOM,
        },
      };
    });

    setIsMetric(newIsMetric);
  }

  // ─── Auto-assign Unit on both tabs when unit system toggles ─────────────────
  useEffect(() => {
    setEstimationTable((prev) => {
      const tabsToCheck = ["Bulk Materials", "Tag Materials"];
      let updatedRevision = { ...(prev?.[revision] || {}) };
      let changed = false;

      tabsToCheck.forEach((tab) => {
        const rows = updatedRevision[tab] || [];
        if (!rows.length) return;

        let tabChanged = false;
        const updatedRows = rows.map((row) => {
          // First normalize any legacy values (e.g. "Ft" → "ft", "No." → "Nos")
          const normalized = normalizeUnit(row.Unit);
          const correctUnit = getUnitForComponent(row.Components, isMetric);
          const finalUnit = correctUnit || normalized || "";
          if (row.Unit === finalUnit) return row;
          tabChanged = true;
          return { ...row, Unit: finalUnit };
        });

        if (tabChanged) {
          updatedRevision = { ...updatedRevision, [tab]: updatedRows };
          changed = true;
        }
      });

      if (!changed) return prev;

      return { ...prev, [revision]: updatedRevision };
    });
  }, [revision, isMetric]);

  // ─── API ─────────────────────────────────────────────────────────────────────
  const api = {
    getItems: () => estimationTable[revision]?.[currentTab] || [],
  };

  // ─── Handlers ────────────────────────────────────────────────────────────────
  function handleAddRow() {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || []), {}];
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleFieldUpdate(rowIndex, fieldName, value) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab[rowIndex] = { ...updatedTab[rowIndex], [fieldName]: value };
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  function handleDeleteRow(rowIndex) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const updatedTab = [...(updatedRevision[currentTab] || [])];
      updatedTab.splice(rowIndex, 1);
      return {
        ...prev,
        [revision]: { ...updatedRevision, [currentTab]: updatedTab },
      };
    });
  }

  // ─── Summary handlers ────────────────────────────────────────────────────────
  // rowOriginal is one of buildSummaryRows' display rows (see utils.jsx). Every
  // row is a fixed slot — "fixed-manual" rows (1.x, 3.3 Hydrotest, 4.x) persist
  // their Unit/Est Unit/rate keyed by id in Summary.manualRows; "computed" rows
  // (2.1/2.2/3.1/3.2) only persist their rate, keyed by id, in Summary.rates.
  function handleSummaryFieldUpdate(rowOriginal, field, value) {
    setEstimationTable((prev) => {
      const updatedRevision = { ...(prev[revision] || {}) };
      const summary = { ...(updatedRevision.Summary || {}) };

      if (rowOriginal.kind === "fixed-manual") {
        const manualRows = { ...(summary.manualRows || {}) };
        manualRows[rowOriginal.id] = {
          ...(manualRows[rowOriginal.id] || {}),
          [field]: value,
        };
        summary.manualRows = manualRows;
      } else if (rowOriginal.kind === "computed" && field === "estUnitRate") {
        summary.rates = { ...(summary.rates || {}), [rowOriginal.id]: value };
      }

      updatedRevision.Summary = summary;
      return { ...prev, [revision]: updatedRevision };
    });
  }

  // ─── BOM generation (accepts fresh rows directly) ────────────────────────────
  function generateBOMData(bulkMaterials, tagMaterials) {
    const allMaterials = [...bulkMaterials, ...tagMaterials];

    return allMaterials
      .filter((row) => (Number(row.Qty) || 0) > 0) // skip rows with no qty entirely
      .map((row, index) => {
        const unit = row.Unit || "No.";
        const qty = Number(row.Qty) || 0;

        let totalWeight = row.TotalWeight;
        if (
          totalWeight === "N/A" ||
          totalWeight === undefined ||
          totalWeight === null
        ) {
          totalWeight = 0;
        } else {
          totalWeight = Number(totalWeight) || 0;
        }

        const isNotNos = unit !== "Nos" && unit !== "ft²" && unit !== "m²";

        // return {
        //   ItemNo: index + 1,
        //   MatDesc: row["Material Description"] || "",
        //   CategoryType: row["CategoryType"] || "",
        //   Quantity: isNotNos ? "-" : qty,
        //   dimension: isNotNos ? qty : "-",
        //   ItemUnit: unit,
        //   totWeight: totalWeight ? totalWeight.toFixed(2) : 0,
        // };
        const bomRow = {
          ItemNo: index + 1,
          MatDesc: row["Material Description"] || "",
          CategoryType: row["CategoryType"] || "",
          Quantity: isNotNos ? "-" : qty,
          dimension: isNotNos ? qty : "-",
          ItemUnit: unit,
          totWeight: totalWeight ? totalWeight.toFixed(2) : 0,
          uid: row["UID"] || "",
          tagNumber:
            row.CategoryType !== "Bulk" ? row["TagNumber"] || "" : undefined,
        };

        console.log("BOM row:", bomRow);
        return bomRow;
      });
  }

  // ─── Estimate ────────────────────────────────────────────────────────────────
  async function handleEstimate() {
    // estimate() returns metric — convert every estimated row to imperial
    const toImperial = (row) => {
      if ((Number(row.Qty) || 0) === 0) return row;

      const next = { ...row };

      if (
        next.Weight !== undefined &&
        next.Weight !== null &&
        next.Weight !== "" &&
        next.Weight !== "N/A"
      ) {
        next.Weight = +(Number(next.Weight) * 2.20462).toFixed(4);
      }

      if (
        next.TotalWeight !== undefined &&
        next.TotalWeight !== null &&
        next.TotalWeight !== "" &&
        next.TotalWeight !== "N/A"
      ) {
        next.TotalWeight = +(Number(next.TotalWeight) * 2.20462).toFixed(4);
      }

      return next;
    };

    const currentRevisionData = estimationTable?.[revision] || {};

    const runEstimate = async (tabName) => {
      const rows = currentRevisionData?.[tabName] || [];

      const updatedRows = rows.map((row) => {
        if ((Number(row.Qty) || 0) === 0) {
          return {
            ...row,
            Weight: undefined,
            TotalWeight: undefined,
            TotalFabHours: undefined,
            WeldHour: undefined,
            BoltupHour: undefined,
          };
        }

        return { ...row };
      });

      if (!updatedRows.length) return updatedRows;

      const indexMap = [];

      const rowsToEstimate = updatedRows.filter((row, originalIdx) => {
        if ((Number(row.Qty) || 0) > 0) {
          indexMap.push(originalIdx);
          return true;
        }

        return false;
      });

      if (!rowsToEstimate.length) return updatedRows;

      await estimate(
        rowsToEstimate,
        (subIndex, fieldName, value) => {
          const originalIndex = indexMap[subIndex];

          if (originalIndex === undefined) return;

          updatedRows[originalIndex] = {
            ...updatedRows[originalIndex],
            [fieldName]: value,
          };
        },
        fittingsWeld,
        pipeWeld,
      );

      return updatedRows;
    };

    const freshBulkMetric = await runEstimate("Bulk Materials");
    const freshTagMetric = await runEstimate("Tag Materials");

    // Convert metric → imperial and re-derive units
    const freshBulk = freshBulkMetric.map(toImperial);
    const freshTag = freshTagMetric.map(toImperial);

    const bomData = generateBOMData(freshBulk, freshTag);

    setEstimationTable((prev) => ({
      ...prev,
      [revision]: {
        ...(prev[revision] || {}),
        "Bulk Materials": freshBulk,
        "Tag Materials": freshTag,
        BOM: bomData,
      },
    }));

    // Estimated values must only become visible after the user clicks Estimate.
    setShowEstimatedBulkTable(true);

    // Reset toggle to imperial after estimate
    setIsMetric(false);
  }

  // ─── Send BOM to PLM ─────────────────────────────────────────────────────────
  function handleSendBomToPlm() {
    // Read BOM from state (already generated and saved by handleEstimate)
    const bom = estimationTable[revision]?.BOM || [];

    const payload = {
      source: "Estimation",
      softwareId: uuidv4(),
      name: "Piping",
      dwgNumber: "001",
      dwgTitle: "Piping BOM",
      revision: revision,
      version: "673",
      sheetNumber: "0",
      sheetTitle: "Sheet",
      projectId: id,
      materials: bom.map((cur) => ({
        item: cur.ItemNo,
        ref: null,
        description: cur.MatDesc,
        length:
          cur.ItemUnit === UNITS_ENUM.Ft && cur.dimension !== "-"
            ? cur.dimension
            : null,
        quantity:
          cur.Quantity === "-"
            ? null
            : cur.ItemUnit === UNITS_ENUM.no
              ? 1
              : cur.Quantity,
        weightlb: cur.totWeight === "-" ? null : cur.totWeight,
        costCode: null,
        softwareId: uuidv4(),
      })),
    };

    secondAxios.delete("drawing/title/Structure BOM");
    secondAxios.post("drawing", [payload]);
  }

  const allColumnsByTab = columns(
    allTables,
    SelectedParts,
    handleFieldUpdate,
    handleDeleteRow,
    isMetric,
    dataSheetsStore,
    uidOptions,
    uidRows,
    specLibraryTable,
  );

  const summaryTableColumns = summaryColumns(handleSummaryFieldUpdate);

  const summaryRows = useMemo(
    () =>
      buildSummaryRows({
        bulkRows: estimationTable[revision]?.["Bulk Materials"] || [],
        tagRows: estimationTable[revision]?.["Tag Materials"] || [],
        isMetric,
        summary: estimationTable[revision]?.Summary,
        wbsUid,
      }),
    [estimationTable, revision, isMetric, wbsUid],
  );

  const standaloneColumns =
    currentTab === "Summary" || currentTab === "BOM"
      ? withStandaloneTableStyles(allColumnsByTab[currentTab] || [])
      : [];

  const allBulkColumns = allColumnsByTab["Bulk Materials"];
  const allTagColumns = allColumnsByTab["Tag Materials"];

  // Keys that belong in the main (input) panel vs. the "Estimated values" panel —
  // shared by both Bulk Materials and Tag Materials so the two tabs look alike.
  const FIRST_PANEL_KEYS = [
    "UID",
    "Spec",
    "Line Number",
    "Components",
    "Size",
    "Schedule/Rating",
    "Ends",
    "Material",
    "Material Description",
    "Qty",
    "Unit",
    "Remarks",
  ];

  const SECOND_PANEL_KEYS = [
    "Weight",
    "WeldHour",
    "BoltupHour",
    "TotalWeight",
    "TotalFabHours",
    "Actions",
  ];

  const firstBulkColumns = withPanelCellStyles(
    allBulkColumns.filter((column) =>
      FIRST_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    true,
  );

  const secondBulkColumns = withPanelCellStyles(
    allBulkColumns.filter((column) =>
      SECOND_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    false,
  );

  // Tag Materials keeps its own extra columns (Tag Number, Data Sheet) in the
  // main panel alongside the shared fields.
  const firstTagColumns = withPanelCellStyles(
    allTagColumns.filter((column) => {
      const key = column.accessorKey || column.id;
      return (
        FIRST_PANEL_KEYS.includes(key) ||
        key === "TagNumber" ||
        key === "DataSheet"
      );
    }),
    true,
  );

  const secondTagColumns = withPanelCellStyles(
    allTagColumns.filter((column) =>
      SECOND_PANEL_KEYS.includes(column.accessorKey || column.id),
    ),
    false,
  );

  const renderBulkMaterialsTable = (
    tableColumns,
    tableData,
    label,
    scrollRef,
    onScroll,
  ) => {
    // Both the main panel (Bulk/Tag Materials) and the "Estimated values"
    // panel use identical vertical metrics below so their rows stay level.
    const isFirstPanel = label !== "Estimated values";
    console.log("estimation table rendered");
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          // Estimated values is pinned to just enough width for its numeric
          // columns; the materials table takes everything that's left.
          ...(isFirstPanel
            ? {
                flex: {
                  xs: "1 1 100%",
                  md: "1 1 0",
                },

                width: "100%",
              }
            : {
                flex: {
                  xs: "1 1 100%",
                  md: `0 0 ${ESTIMATED_PANEL_WIDTH}px`,
                },

                width: {
                  xs: "100%",
                  md: `${ESTIMATED_PANEL_WIDTH}px`,
                },

                maxWidth: "100%",
              }),
          minWidth: 0,
        }}
      >
        {label && (
          <Typography
            variant="subtitle2"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              px: 1,
              py: 0.5,
            }}
          >
            {label}
          </Typography>
        )}

        <MaterialReactTable
          columns={tableColumns}
          data={tableData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableStickyHeader={true}
          enableSorting={false}
          enableColumnActions={false}
          {...mrtSurfaceProps}
          /*
           * tableLayout: fixed + width: 100% makes the browser scale the
           * columns' declared sizes to exactly fill the panel — so the columns
           * are never spread apart by leftover space, and never overflow into
           * a horizontal scrollbar either.
           */
          muiTableProps={{
            sx: {
              tableLayout: "fixed",
              width: "100%",
            },
          }}
          muiTableContainerProps={{
            ref: scrollRef,
            onScroll,

            sx: {
              height: PANEL_BODY_MAX_HEIGHT,
              maxHeight: PANEL_BODY_MAX_HEIGHT,

              width: "100%",
              minWidth: 0,
              maxWidth: "100%",

              overflowX: "hidden",

              backgroundColor: "background.paper",

              /*
               * Final enforcement on actual MRT-rendered cells.
               */
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: (theme) =>
                  `${estimationHeaderBackground(theme)} !important`,

                borderRight: `${ESTIMATION_TABLE_BORDER} !important`,

                borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,
              },

              "& .MuiTableBody-root .MuiTableCell-root": {
                borderRight: `${ESTIMATION_TABLE_BORDER} !important`,

                borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,

                fontSize: `${PANEL_FONT_SIZE} !important`,

                fontWeight: "500 !important",

                textAlign: "center !important",
              },

              "& .MuiTableBody-root .Mui-TableBodyCell-Content-Wrapper": {
                width: "100%",

                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",

                textAlign: "center !important",

                fontSize: `${PANEL_FONT_SIZE} !important`,
              },
            },
          }}
          /*
           * Same fixed row height in both panels — this is what lets the
           * synced scroll keep matching rows on the same visual line.
           */
          muiTableBodyRowProps={{
            sx: {
              height: `${PANEL_ROW_HEIGHT}px !important`,
            },
          }}
          /*
           * These only apply to columns that carry no sx of their own — every
           * real column gets the same styling folded in by withPanelCellStyles
           * above, because MRT's shallow prop merge would otherwise drop it.
           */
          muiTableBodyCellProps={{ sx: panelBodyCellSx(isFirstPanel) }}
          muiTableHeadCellProps={{ sx: panelHeadCellSx }}
          muiTableHeadRowProps={{
            sx: {
              backgroundColor: (theme) =>
                `${estimationHeaderBackground(theme)} !important`,

              height: `${PANEL_HEAD_HEIGHT}px !important`,

              minHeight: `${PANEL_HEAD_HEIGHT}px !important`,

              maxHeight: `${PANEL_HEAD_HEIGHT}px !important`,
            },
          }}
          initialState={{
            pagination: {
              pageSize: 1000,
              pageIndex: 0,
            },
          }}
          state={{
            columnOrder: tableColumns.map((col) => col.accessorKey || col.id),
          }}
        />
      </Box>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider" }}
    >
      <Tabs
        value={tabIndex}
        onChange={(e, newIndex) => setTabIndex(newIndex)}
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {tabLabels.map((label) => (
          <Tab label={label} key={label} />
        ))}
      </Tabs>

      <Box
        py={2}
        sx={{
          display: "flex",

          flexDirection: {
            xs: "column",
            md: "row",
          },

          alignItems: {
            xs: "stretch",
            md: "center",
          },

          justifyContent: "space-between",

          gap: 1.5,

          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",

            flexWrap: "wrap",

            gap: 1.5,
            alignItems: "center",

            flex: 1,

            minWidth: 0,
          }}
        >
          {(currentTab === "Bulk Materials" ||
            currentTab === "Tag Materials") && (
            <>
              <Button
                variant="outlined"
                onClick={handleAddRow}
                sx={{ height: 40, fontSize: "0.9rem", px: 3, fontWeight: 600 }}
              >
                Add Row
              </Button>

              <Button
                color={showEstimatedBulkTable ? "secondary" : "primary"}
                variant="contained"
                onClick={handleEstimate}
                sx={{
                  height: 40,
                  fontSize: "0.9rem",
                  px: 3,
                  fontWeight: 600,
                }}
              >
                Estimate
              </Button>

              {currentTab === "Bulk Materials" && (
                <>
                  <TextField
                    defaultValue={fittingsWeld}
                    onBlur={(e) => setFittingsWeld(e.target.value)}
                    type="number"
                    label="Fittings to Fittings Weld"
                    size="small"
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { fontSize: "0.9rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    }}
                  />
                  <TextField
                    defaultValue={pipeWeld}
                    onBlur={(e) => setPipeWeld(e.target.value)}
                    type="number"
                    label="Pipe to Pipe Weld"
                    size="small"
                    sx={{
                      minWidth: 180,
                      "& .MuiInputBase-root": { fontSize: "0.9rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    }}
                  />
                </>
              )}

              <UnitToggle isMetric={isMetric} onChange={handleUnitToggle} />
            </>
          )}
        </Box>

        {currentTab === "BOM" && (
          <Button variant="contained" onClick={handleSendBomToPlm}>
            Send BOM to PLM
          </Button>
        )}
      </Box>

      {currentTab === "Bulk Materials" || currentTab === "Tag Materials" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1 }}>
          <Box
            sx={{
              display: "flex",

              flexDirection: {
                xs: "column",
                md: "row",
              },

              gap: 1.5,

              alignItems: "flex-start",

              width: "100%",
              minWidth: 0,
              maxWidth: "100%",

              overflowX: "hidden",
            }}
          >
            {renderBulkMaterialsTable(
              currentTab === "Bulk Materials"
                ? firstBulkColumns
                : firstTagColumns,
              estimationTable[revision]?.[currentTab] || [],
              currentTab,
              firstPanelScrollRef,
              syncPanelScroll(firstPanelScrollRef, secondPanelScrollRef),
            )}

            {showEstimatedBulkTable &&
              renderBulkMaterialsTable(
                currentTab === "Bulk Materials"
                  ? secondBulkColumns
                  : secondTagColumns,
                estimationTable[revision]?.[currentTab] || [],
                "Estimated values",
                secondPanelScrollRef,
                syncPanelScroll(secondPanelScrollRef, firstPanelScrollRef),
              )}
          </Box>
        </Box>
      ) : currentTab === "Summary" ? (
        <MaterialReactTable
          columns={summaryTableColumns}
          data={summaryRows}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableStickyHeader={true}
          enableSorting={false}
          {...mrtSurfaceProps}
          muiTableContainerProps={{
            sx: { maxHeight: 600, backgroundColor: "background.paper" },
          }}
          muiTableBodyCellProps={{
            sx: { ...mrtBodyCellSx, borderRight: 1, fontSize: "0.85rem" },
          }}
          muiTableHeadCellProps={{
            sx: {
              ...mrtHeadCellSx,
              fontWeight: "bold",
              fontSize: "0.7rem",
              borderRight: 1,
            },
          }}
          initialState={{ pagination: { pageSize: 100, pageIndex: 0 } }}
        />
      ) : (
        <MaterialReactTable
          {...mrtSurfaceProps}
          columns={standaloneColumns}
          data={estimationTable[revision]?.[currentTab] || []}
          enableTopToolbar={false}
          enableStickyHeader
          enableSorting={false}
          enableColumnActions={false}
          layoutMode="grid"
          defaultColumn={{
            minSize: 1,
            maxSize: 100000,
            grow: true,
          }}
          muiTableProps={{
            sx: {
              width: "100% !important",
              minWidth: "100% !important",
              maxWidth: "100% !important",

              overflow: "hidden",
            },
          }}
          muiTableContainerProps={{
            sx: {
              width: "100%",
              maxWidth: "100%",
              maxHeight: 600,

              overflowX: "hidden",

              backgroundColor: "background.paper",

              "& .MuiTableHead-root": {
                width: "100% !important",
              },

              "& .MuiTableBody-root": {
                width: "100% !important",
              },

              "& .MuiTableRow-root": {
                width: "100% !important",
              },

              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: (theme) =>
                  `${estimationHeaderBackground(theme)} !important`,

                borderRight: `${ESTIMATION_TABLE_BORDER} !important`,

                borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,
              },

              "& .MuiTableBody-root .MuiTableCell-root": {
                borderRight: `${ESTIMATION_TABLE_BORDER} !important`,

                borderBottom: `${ESTIMATION_TABLE_BORDER} !important`,

                textAlign: "center !important",

                fontSize: `${PANEL_FONT_SIZE} !important`,
              },

              "& .MuiTableBody-root .Mui-TableBodyCell-Content-Wrapper": {
                width: "100%",

                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",

                textAlign: "center !important",
              },
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              height: `${PANEL_ROW_HEIGHT}px !important`,
            },
          }}
          muiTableHeadRowProps={{
            sx: {
              height: `${PANEL_HEAD_HEIGHT}px !important`,

              backgroundColor: (theme) =>
                `${estimationHeaderBackground(theme)} !important`,
            },
          }}
          muiTableBodyCellProps={{
            sx: panelBodyCellSx(true),
          }}
          muiTableHeadCellProps={{
            sx: panelHeadCellSx,
          }}
          initialState={{
            pagination: {
              pageSize: 50,
              pageIndex: 0,
            },
          }}
          state={{
            columnOrder: standaloneColumns.map(
              (col) => col.accessorKey || col.id,
            ),
          }}
        />
      )}
    </Box>
  );
}
